import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from './ui/input'
import { useContracts } from '../hooks/useQueries'
import { useDebounce } from '../hooks/useDebounce'

/**
 * A minimal "pick one contract" control: a search-as-you-type box over the
 * org's contracts and a short result list, modelled on `AssetPicker`. Used by
 * the interventions list to filter on a contract (spec 2026-10-11-38).
 * Strings live in the `interventions` namespace (`list.contract_picker_*`).
 */
export function ContractPicker({
  org,
  onChange,
  testId = 'contract-picker',
}: {
  org: string
  /** `name` is the contract's display name, handy as the badge label. */
  onChange: (uid: string, name: string) => void
  testId?: string
}) {
  const { t } = useTranslation('interventions')
  const [term, setTerm] = useState('')
  const debounced = useDebounce(term, 250).trim()
  const enabled = Boolean(org) && debounced.length >= 2
  const { data: contracts = [], isFetching, isError } = useContracts(org, {
    search: debounced,
    limit: 10,
    enabled,
  })
  const results = enabled ? contracts.slice(0, 10) : []

  return (
    <div className="relative w-[220px]" data-testid={testId}>
      <Input
        placeholder={t('list.contract_picker_placeholder')}
        aria-label={t('list.contract_picker_placeholder')}
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape' && term) { e.preventDefault(); setTerm('') } }}
        data-testid={`${testId}-input`}
      />
      {enabled && (
        <div className="absolute z-20 mt-1 w-72 rounded-md border bg-popover shadow-md">
          {isError ? (
            <p className="p-2 text-xs text-destructive" data-testid={`${testId}-error`}>{t('list.contract_picker_error')}</p>
          ) : isFetching && results.length === 0 ? (
            <p className="p-2 text-xs text-muted-foreground" data-testid={`${testId}-loading`}>{t('list.contract_picker_loading')}</p>
          ) : results.length === 0 ? (
            <p className="p-2 text-xs text-muted-foreground" data-testid={`${testId}-empty`}>{t('list.contract_picker_empty', { term: debounced })}</p>
          ) : (
            <ul className="max-h-48 divide-y overflow-y-auto">
              {results.map((c) => (
                <li key={c.uid}>
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm hover:bg-muted"
                    data-testid={`${testId}-option-${c.uid}`}
                    onClick={() => { onChange(c.uid, c.name || c.code || ''); setTerm('') }}
                  >
                    <div>{c.name || c.code}</div>
                    {c.name && c.code && <div className="text-xs text-muted-foreground">{c.code}</div>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
